import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';


import { HttpHeaders } from '@angular/common/http';
import {ApiService} from "../../../api.service";
import {shareReplay, tap} from "rxjs/operators";
import {TalentHappyPathAuthentication} from "./talent-happy-path-auth.service";
import {TalentHappyPathAuthService} from "./talent-happy-path-auth.service";
import { TalentHappyPathDataService } from "./talent-happy-path-data-service";
import { VideoInterviewStepTokenService } from "../../initial-interview/video-interview/service/video-interview-step-token.service";

export interface ErrorResponse {
  error: string;
  errorId: string;
  errorDescription: string;
}

export function isErrorResponse(obj: any): obj is ErrorResponse {
  return typeof obj === 'object' && obj !== null && 'error' in obj;
}

@Injectable({
  providedIn: 'root'
})
export class TalentHappyPathAuthApiService {
  constructor(private apiService: ApiService,
              private authService: TalentHappyPathAuthService,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private videoInterviewStepTokenService: VideoInterviewStepTokenService) {}

  searchTalent(email: string, jobPostingId?: number): Observable<Talent> {
    const formData = new FormData();
    formData.append('email', email);
    if (jobPostingId != null) {
      formData.append('jobPostingId', String(jobPostingId));
    }
    return this.apiService.httpWrapper<Talent>({
      method: 'POST',
      url: '/api/authentication/search',
      body: formData
    });
  }

  searchTalentByToken(token: string, jobPostingId?: string): Observable<Talent> {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('jobPostingId', jobPostingId);
    return this.apiService.httpWrapper<Talent>({
      method: 'POST',
      url: '/api/authentication/search-by-token',
      body: formData
    }).pipe(shareReplay(1));
  }

  login(userCredentials: QuickApplyUserCredentials) {
    this.authService.clear();
    // Whoever is signing in now owns the rest of this visit. A video interview step token left over
    // from a previous person in this tab would name them instead, and the video proxy refuses a
    // request whose two credentials disagree (BIZ-40996). Safe to drop unconditionally: a returning
    // talent's own person id rides in the token they are about to be issued, and a brand-new
    // applicant is only given a step token later, once their application creates the person.
    this.videoInterviewStepTokenService.clear();
    return this.apiService.httpWrapper<string>({
      method: 'POST',
      url: '/api/authentication/auth',
      body: userCredentials
    }).pipe(tap(resp => {
      this.setAuthentication(resp);
    }));
  }

  refreshToken() {
    return this.apiService.httpWrapper<string>({
      method: 'POST',
      url: '/api/authentication/refresh'
    }).pipe(tap(resp => {
      this.setAuthentication(resp);
      shareReplay(1);
    }));
  }

  private setAuthentication(authResult) {
    const auth: TalentHappyPathAuthentication = {};
    auth.id_token = authResult.token;
    auth.expiration_date = authResult.expirationDate;
    this.authService.authentication = auth;
  }

  resendOneTimePasscode(email: String): Observable<boolean> {
    return this.apiService.httpWrapper<boolean>({
      method: 'GET',
      url: `/api/authentication/resendPasscode/${email}`
    })
  }
}
